// src/utils/metrics/progression.js
import { CLIMBING_STYLES } from '../constants.js';
import { calculateCGP, calculateCGE } from './cgp.js';

export const calculateProgressionData = (data, climbingStyle = CLIMBING_STYLES.SPORT) => {
  if (!data?.length) return [];
  
  // Get all data for CGE calculation (not just clean ascents)
  const allData = data
    .filter(ascent => {
      const grade = climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks;
      return grade !== null && !isNaN(grade);
    })
    .sort((a, b) => a['Ascent Date'].localeCompare(b['Ascent Date']));

  const sortedData = data
    .filter(ascent => {
      const grade = climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks;
      return ascent.isClean && grade !== null && !isNaN(grade);
    })
    .sort((a, b) => a['Ascent Date'].localeCompare(b['Ascent Date']));

  if (!sortedData?.length) return [];
  
  const allGrades = sortedData.map(ascent => 
    climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks
  );
  const minGrade = Math.min(...allGrades);
  const maxGrade = Math.max(...allGrades);


  // Build all months from first ascent's year/month to last ascent's year/month (UTC-correct)
  const first = sortedData[0];
  const last = sortedData[sortedData.length - 1];

  const allMonths = [];
  let y = first.year, m = first.month;
  const endY = last.year, endM = last.month;
  while (y < endY || (y === endY && m <= endM)) {
    allMonths.push({
      key: `${y}-${String(m).padStart(2, '0')}`,
      date: new Date(y, m - 1, 1),
      ascents: [],
      allAscents: []
    });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }

  const monthsByKey = Object.fromEntries(allMonths.map(x => [x.key, x]));
  const monthKeyFor = (ascent) => `${ascent.year}-${String(ascent.month).padStart(2, '0')}`;

  sortedData.forEach(ascent => {
    const md = monthsByKey[monthKeyFor(ascent)];
    if (md) md.ascents.push(ascent);
  });

  allData.forEach(ascent => {
    const md = monthsByKey[monthKeyFor(ascent)];
    if (md) md.allAscents.push(ascent);
  });

  const progressData = [];
  let runningMaxGrade = -Infinity;
  
  // Process each month's data
  allMonths.forEach((monthData, monthIndex) => {
    const monthAscents = monthData.ascents;
    
    // Calculate this month's metrics if we have ascents
    let monthAvgGrade = null;
    if (monthAscents.length > 0) {
      const grades = monthAscents.map(ascent => 
        climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks
      );
      
      // Calculate max grade (maintain running max)
      const monthMaxGrade = Math.max(...grades);
      runningMaxGrade = Math.max(runningMaxGrade, monthMaxGrade);
      
      // Calculate average grade for the month
      monthAvgGrade = grades.reduce((sum, grade) => sum + grade, 0) / grades.length;
    }
    
    // Calculate rolling averages (12-month window) if we have enough history
    let rollingAvgGrade = null;
    let rollingCGPPerDay = null;
    let rollingCGEPerDay = null;
    
    if (monthIndex >= 11) {
      // Get all ascents from the previous 12 months
      const last12Months = allMonths.slice(monthIndex - 11, monthIndex + 1);
      const last12MonthsAscents = last12Months.flatMap(m => m.ascents);
      const last12MonthsAllAscents = last12Months.flatMap(m => m.allAscents);
      
      if (last12MonthsAscents.length > 0) {
        // Calculate rolling grade average
        const last12MonthsGrades = last12MonthsAscents.map(ascent => 
          climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks
        );
        
        rollingAvgGrade = last12MonthsGrades.reduce((sum, grade) => sum + grade, 0) / 
          last12MonthsGrades.length;

        // Calculate rolling CGP per day
        const cgp = calculateCGP(last12MonthsAscents, climbingStyle);
        
        // Count unique climbing days in the last 12 months
        const uniqueDays = new Set(
          last12MonthsAscents.map(ascent => 
            ascent['Ascent Date'].slice(0, 10)
          )
        );
        
        if (uniqueDays.size > 0) {
          if (climbingStyle === CLIMBING_STYLES.BOULDER) {
            rollingCGPPerDay = Math.log(Math.pow(Math.E, cgp) / uniqueDays.size);
          } else {
            rollingCGPPerDay = Math.log2(Math.pow(2, cgp) / uniqueDays.size);
          }
        }

        // Calculate rolling CGE per day
        if (last12MonthsAllAscents.length > 0) {
          const cge = calculateCGE(last12MonthsAllAscents, climbingStyle);
          
          // Count unique climbing days for all ascents
          const allUniqueDays = new Set(
            last12MonthsAllAscents.map(ascent => 
              ascent['Ascent Date'].slice(0, 10)
            )
          );
          
          if (allUniqueDays.size > 0) {
            if (climbingStyle === CLIMBING_STYLES.BOULDER) {
              rollingCGEPerDay = Math.log(Math.pow(Math.E, cge) / allUniqueDays.size);
            } else {
              rollingCGEPerDay = Math.log2(Math.pow(2, cge) / allUniqueDays.size);
            }
          }
        }
      }
    }

    progressData.push({
      date: monthData.date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short'
      }),
      maxCleanGrade: runningMaxGrade,
      avgCleanGrade: monthAvgGrade ? parseFloat(monthAvgGrade.toFixed(1)) : null,
      rollingAvgGrade: rollingAvgGrade ? parseFloat(rollingAvgGrade.toFixed(1)) : null,
      rollingCGPPerDay: rollingCGPPerDay ? parseFloat(rollingCGPPerDay.toFixed(2)) : null,
      rollingCGEPerDay: rollingCGEPerDay ? parseFloat(rollingCGEPerDay.toFixed(2)) : null
    });
  });
  
  return {
    progressData,
    yDomain: [minGrade - 0.5, maxGrade + 0.5]
  };
};